import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  notificationsService,
  type NotificationSettings,
} from "@/services/supabase/notifications";
import { useToast } from "@/hooks/use-toast";

// Get all notifications
export function useNotifications(userId?: string, limit = 5, offset = 0) {
  return useQuery({
    queryKey: ["notifications", "v2", userId, limit, offset],
    queryFn: () =>
      notificationsService.getNotifications(userId!, limit, offset),
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// Get unread count
export function useUnreadCount(userId?: string) {
  return useQuery({
    queryKey: ["notifications", "unreadCount", userId],
    queryFn: () => notificationsService.getUnreadCount(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
}

// Mark single notification as read
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: notificationsService.markAsRead,
    onMutate: async (notificationId) => {
      // Optimistically update UI
      const previousData = queryClient.getQueryData(["notifications"]);

      // Update the notification in the cache
      queryClient.setQueriesData(
        { queryKey: ["notifications"] },
        (oldData: any) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((notification: any) =>
              notification.id === notificationId
                ? {
                    ...notification,
                    read: true,
                    read_at: new Date().toISOString(),
                  }
                : notification
            ),
          };
        }
      );

      // Decrement unread count optimistically
      const userId = queryClient.getQueryData(["user"])?.id;
      if (userId) {
        const previousCount = queryClient.getQueryData([
          "notifications",
          "unreadCount",
          userId,
        ]);
        if (typeof previousCount === "number" && previousCount > 0) {
          queryClient.setQueryData(
            ["notifications", "unreadCount", userId],
            previousCount - 1
          );
        }
      }

      return { previousData };
    },
    onError: (error, _, context) => {
      // Revert to previous state if mutation fails
      if (context?.previousData) {
        queryClient.setQueriesData(
          { queryKey: ["notifications"] },
          context.previousData
        );
      }

      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", "unreadCount"],
      });
    },
  });
}

// Mark all as read
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: notificationsService.markAllAsRead,
    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      await queryClient.cancelQueries({
        queryKey: ["notifications", "unreadCount", userId],
      });

      const previousNotifications = queryClient.getQueryData(["notifications"]);

      // Update all notifications to read
      queryClient.setQueriesData(
        { queryKey: ["notifications"] },
        (oldData: any) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((notification: any) => ({
              ...notification,
              read: true,
              read_at: new Date().toISOString(),
            })),
          };
        }
      );

      // Set unread count to 0
      queryClient.setQueryData(["notifications", "unreadCount", userId], 0);

      return { previousNotifications };
    },
    onError: (error, _, context) => {
      // Revert on error
      if (context?.previousNotifications) {
        queryClient.setQueriesData(
          { queryKey: ["notifications"] },
          context.previousNotifications
        );
      }

      toast({
        title: "Error",
        description: "Failed to mark all notifications as read",
        variant: "destructive",
      });
    },
    onSettled: (_, __, userId) => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", "unreadCount", userId],
      });
    },
  });
}

// Delete notification
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: notificationsService.deleteNotification,
    onSuccess: () => {
      // Invalidate notification queries
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", "unreadCount"],
      });

      toast({
        title: "Notification deleted",
        description: "The notification has been deleted.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to delete notification",
        variant: "destructive",
      });
    },
  });
}

// Get notification settings
export function useNotificationSettings(userId?: string) {
  return useQuery({
    queryKey: ["notificationSettings", userId],
    queryFn: () => notificationsService.getNotificationSettings(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Update notification settings
export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      userId,
      settings,
    }: {
      userId: string;
      settings: Partial<NotificationSettings>;
    }) => notificationsService.updateNotificationSettings(userId, settings),
    onSuccess: (data, variables) => {
      // Update the cache
      queryClient.setQueryData(
        ["notificationSettings", variables.userId],
        data
      );

      toast({
        title: "Settings updated",
        description: "Your notification settings have been updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to update notification settings",
        variant: "destructive",
      });
    },
  });
}
